import {SubAccountDebtCollectionSettingsSchema} from '@/debt-collection/KraviaReminder'
import {ContactInfoSchema} from '@/subaccount/types/ContactInfo'
import {AddressSchema} from '@/subaccount/types/Address'
import {z} from 'zod'
import {orgNoRegex} from '@/services/validators.service'
import {SupplierBankSchema} from '@/bank/SupplierBankSchema'
import {nullFieldsToNull} from '@/validation-utils/ZodHelpers'

export const SubAccountRelationTypeEnum = z.enum(['CUSTOMER', 'SUPPLIER'], 'Må velge kunde eller leverandør')
export type SubAccountRelationType = z.infer<typeof SubAccountRelationTypeEnum>
export const SUPPLIER = SubAccountRelationTypeEnum.enum.SUPPLIER
export const CUSTOMER = SubAccountRelationTypeEnum.enum.CUSTOMER

const superrefiner = (subAccount: NewSubAccount, ctx: { addIssue: Function }) => {

    const assumedNorwegian = subAccount.address?.countryCode === 'NO' || !subAccount.address
    if (!assumedNorwegian) {
        return
    }
    if (!subAccount.isBusiness && subAccount.orgNo) {
        ctx.addIssue({
            path: ['orgNo'],
            code: 'custom',
            message: 'Ikke mulig å angi organisasjonsnummer for privatpersoner med norsk adresse'
        })
    }
    if (subAccount.isBusiness && subAccount.orgNo && !orgNoRegex.test(subAccount.orgNo)) {
        ctx.addIssue({
            path: ['orgNo'],
            code: 'custom',
            message: 'Ugyldig format på norsk organisasjonsnummer'
        })
    }
}


export const SubAccountSchema = z.object({
    uuid: z.uuid('Invalid or missing UUID'),
    subAccountNo: z.number(),
    relationType: SubAccountRelationTypeEnum,
    isBusiness: z.boolean('sub-account.validation.is-business.blank').default(true),
    orgNo: z.string().nullish(),
    name: z.string('sub-account.validation.name.blank').trim().min(3, 'Navn må være minst 3 tegn'),
    contactInfo: z.preprocess(nullFieldsToNull, z.nullish(ContactInfoSchema)),
    address: z.preprocess(nullFieldsToNull, z.nullish(AddressSchema)),
    metadata: z.object({
        suggestions: z.object({
            expenseAccount: z.string().nullish(),
            project: z.string().nullish(),
            department: z.string().nullish()
        }).default({expenseAccount: '', project: '', department: ''})
    }).default({suggestions: {expenseAccount: '', project: '', department: ''}}),
    notes: z.string().optional().default(''),
    active: z.boolean().default(true),
    totalUnpaid: z.number().optional(),
    totalInvoiced: z.number().optional(),
    customerDebtCollectionSettings: SubAccountDebtCollectionSettingsSchema.nullable().optional(),
    ...SupplierBankSchema.shape
}).superRefine(superrefiner)


export type SubAccount = z.infer<typeof SubAccountSchema>

export const NewSubAccountSchema = SubAccountSchema.omit({uuid: true, subAccountNo: true}).superRefine(superrefiner)
export type NewSubAccount = z.infer<typeof NewSubAccountSchema>

export type SubAccountInvoiceBalance = {
    subAccountUuid: string,
    totalUnpaid: number,
    totalInvoiced: number
}

export type AgeDividedReportEntry = {
    subAccountUuid?: string,
    sums: Record<string, number>
}

